import { CheckCircle2, CircleAlert, RefreshCw, ShieldCheck } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

import { useCardholderCopy } from "../hooks/useCardholderCopy";
import { useVerificationSync } from "../hooks/useVerificationSync";
import type { CardholderBoardItem } from "../types/cardholderOnboarding.types";

export interface SyncVerificationDialogProps {
  item: CardholderBoardItem;
  onClose: () => void;
  onSynced: () => void;
  /** No verification is linked after all: hand over to the link dialog. */
  onLinkInstead: () => void;
}

export function SyncVerificationDialog({
  item,
  onClose,
  onSynced,
  onLinkInstead,
}: SyncVerificationDialogProps) {
  const { t } = useTranslations("cardholderOnboarding");
  const copy = useCardholderCopy();
  const sync = useVerificationSync(item.identity_id, t("error_generic"));
  const result = sync.result;
  const busy = sync.phase === "checking" || sync.phase === "applying";
  const reviewing = sync.phase === "review" || sync.phase === "applying";
  const canUpdate = reviewing && result?.category === "mismatch" && !!result.sumsub_status;
  const canRepoint = reviewing && sync.canRepoint;
  // Discovery only links an identity with no record; a vanished applicant still has one, so only the
  // approved sibling (when there is one) can repair it.
  const needsLink = sync.phase === "review" && result?.category === "no_reference";

  const handleApply = async (repoint: boolean) => {
    if (await sync.apply(repoint)) onSynced();
  };

  const reviewText = (): string => {
    if (!result) return t("sync_no_reference");
    switch (result.category) {
      case "mismatch":
        return t("sync_mismatch", {
          remote: result.sumsub_status ? copy.kycStatus(result.sumsub_status) : "—",
          local: result.local_status ? copy.kycStatus(result.local_status) : t("no_kyc"),
        });
      case "in_sync":
        return t("sync_in_sync", { status: result.local_status ? copy.kycStatus(result.local_status) : "—" });
      case "not_reviewed_yet":
        return t("sync_not_reviewed");
      case "no_reference":
        return t("sync_no_reference");
      case "applicant_not_found":
        return t("sync_applicant_not_found");
      case "not_sumsub":
        return t("sync_other_provider");
      default:
        return t("error_generic");
    }
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title={t("sync_title")}
      description={t("sync_description", { name: item.name ?? t("unnamed") })}
      footer={
        sync.phase === "applied" ? (
          <ColocaButton variant="primary" onClick={onClose}>
            {t("close")}
          </ColocaButton>
        ) : sync.phase === "error" ? (
          <>
            <ColocaButton variant="ghost" onClick={onClose}>
              {t("close")}
            </ColocaButton>
            <ColocaButton variant="primary" icon={RefreshCw} onClick={() => void sync.check()}>
              {t("sync_recheck")}
            </ColocaButton>
          </>
        ) : (
          <>
            <ColocaButton variant="ghost" disabled={busy} onClick={onClose}>
              {t("cancel")}
            </ColocaButton>
            {needsLink && (
              <ColocaButton variant="primary" onClick={onLinkInstead}>
                {t("action_link_verification")}
              </ColocaButton>
            )}
            {canRepoint && (
              <ColocaButton
                variant={canUpdate ? "default" : "primary"}
                loading={sync.phase === "applying"}
                disabled={busy}
                onClick={() => void handleApply(true)}
              >
                {t("sync_repoint")}
              </ColocaButton>
            )}
            {canUpdate && result?.sumsub_status && (
              <ColocaButton
                variant="primary"
                icon={ShieldCheck}
                loading={sync.phase === "applying"}
                disabled={busy}
                onClick={() => void handleApply(false)}
              >
                {t("sync_apply", { status: copy.kycStatus(result.sumsub_status) })}
              </ColocaButton>
            )}
          </>
        )
      }
    >
      <div className="space-y-3">
        {sync.phase === "checking" && (
          <div className="space-y-2" aria-busy>
            <p className="text-sm text-muted-foreground">{t("sync_checking")}</p>
            <ColocaSkeleton className="h-14 w-full" />
          </div>
        )}

        {sync.phase === "error" && sync.errorMessage && (
          <ColocaAlert variant="destructive" icon={CircleAlert} text={sync.errorMessage} />
        )}

        {(sync.phase === "review" || sync.phase === "applying") && (
          <>
            <ColocaAlert
              variant={result?.category === "mismatch" ? "warning" : "secondary"}
              icon={CircleAlert}
              text={reviewText()}
            />
            {canRepoint && <p className="text-sm text-muted-foreground">{t("sync_green_sibling")}</p>}
          </>
        )}

        {sync.phase === "applied" &&
          (result?.category === "repointed" || result?.category === "updated" ? (
            <ColocaAlert
              variant="success"
              icon={CheckCircle2}
              text={
                result.category === "repointed"
                  ? t("sync_repointed")
                  : t("sync_updated", {
                      status: result.sumsub_status ? copy.kycStatus(result.sumsub_status) : "—",
                    })
              }
            />
          ) : (
            <ColocaAlert variant="secondary" icon={CircleAlert} text={reviewText()} />
          ))}
      </div>
    </ColocaDialog>
  );
}
